import {AxiosInstance} from "axios";
import {AccountingNoteApiDto, CreateNoteApiDto, SetReminderApiDto, UpdateNoteApiDto} from "./model/rest";
import Note from "../model/note/Note";
import AccountingNote from "../model/invoice/note/AccountingNote";
import {InvoiceEventTypeEnum} from "../model/invoice/event/InvoiceEvent";
import {TransactionEventTypeEnum} from "../model/transaction/event/TransactionEvent";
import {BillEventTypeEnum} from "../model/bill/event/BillEvent";

export default class NoteApi {
	constructor(public readonly axiosInstance: AxiosInstance) { }

	/**
	 * Fetch notes for given lead.
	 * @param {string} leadId lead identifier.
	 * @return {Promise<Note[]>} promise containing notes for given lead.
	 */
	fetchNotes(leadId: string): Promise<Note[]> {
		const url = `/api/web/leads/${leadId}/notes`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(Note.fromJson));
	}

	/**
	 * Create note for lead.
	 * @param {string} leadId lead identifier.
	 * @param {CreateNoteApiDto} data new note data.
	 * @return {Promise<Note>} promise containing newly added note.
	 */
	createNote(leadId: string, data: CreateNoteApiDto): Promise<Note> {
		const url = `/api/web/leads/${leadId}/notes`;
		return this.axiosInstance.post(url, data).then(resp => Note.fromJson(resp.data));
	}

	/**
	 * Update lead note.
	 * @param noteId note identifier.
	 * @param {data} data update single note api data.
	 * @returns {Promise<Note>} Promise containing updated note.
	 */
	updateNote(noteId: string, data: UpdateNoteApiDto): Promise<Note> {
		const url = `/api/web/leads/notes/${noteId}`;
		return this.axiosInstance.put(url, data).then(resp => Note.fromJson(resp.data));
	}

	/**
	 * Delete lead note.
	 * @param {string} noteId note identifier.
	 * @return {Promise<void>} -
	 */
	deleteNote(noteId: string): Promise<void> {
		const url = `/api/web/leads/notes/${noteId}`;
		return this.axiosInstance.delete(url).then(() => {});
	}

	/**
	 * Add reminder to lead note
	 * @param {string} leadId lead identifier.
	 * @param {string} noteId note identifier.
	 * @param {SetReminderApiDto} data
	 * @return {Promise<void>} promise containing notes for given lead.
	 */
	addReminder(leadId: string, noteId: string, data: SetReminderApiDto): Promise<void> {
		const url = `/api/web/leads/${leadId}/notes/${noteId}/reminder`;
		return this.axiosInstance.put(url, data).then(() => {});
	}

	/**
	 * Fetch notes for given job.
	 * @param {string} jobId job identifier.
	 * @return {Promise<Note[]>} promise containing notes for given lead.
	 */
	fetchJobNotes(jobId: string): Promise<Note[]> {
		const url = `/api/web/jobs/${jobId}/notes`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(Note.fromJson));
	}

	/**
	 * Create note for job.
	 * @param {string} jobId job identifier.
	 * @param {CreateNoteApiDto} data new note data.
	 * @return {Promise<Note>} promise containing newly added note.
	 */
	createJobNote(jobId: string, data: CreateNoteApiDto): Promise<Note> {
		const url = `/api/web/jobs/${jobId}/notes`;
		return this.axiosInstance.post(url, data).then(resp => Note.fromJson(resp.data));
	}

	/**
	 * Update job note.
	 * @param {string} jobId job identifier.
	 * @param noteId note identifier.
	 * @param {data} data update single note api data.
	 * @returns {Promise<Note>} Promise containing updated note.
	 */
	updateJobNote(jobId: string, noteId: string, data: UpdateNoteApiDto): Promise<Note> {
		const url = `/api/web/jobs/${jobId}/notes/${noteId}`;
		return this.axiosInstance.put(url, data).then(resp => Note.fromJson(resp.data));
	}

	/**
	 * Delete job note.
	 * @param {string} jobId job identifier.
	 * @param {string} noteId note identifier.
	 * @return {Promise<void>} -
	 */
	deleteJobNote(jobId: string, noteId: string): Promise<void> {
		const url = `/api/web/jobs/${jobId}/notes/${noteId}`;
		return this.axiosInstance.delete(url).then(() => {});
	}

	/**
	 * Pin / unpin job note.
	 * @param {string} jobId job identifier.
	 * @param {string} noteId note identifier.
	 * @param {boolean} pinned
	 * @returns {Promise<Note>} Promise containing updated note.
	 */
	modifyPinningOnJobNote(jobId: string, noteId: string, pinned: boolean): Promise<Note> {
		const url = `/api/web/jobs/${jobId}/notes/${noteId}/pinned`;
		return this.axiosInstance.put(url, {pinned}).then(resp => Note.fromJson(resp.data));
	}


	/**
	 * Pin / unpin note.
	 * @param {string} noteId note identifier.
	 * @param {boolean} pinned
	 * @returns {Promise<Note>} Promise containing updated note.
	 */
	modifyPinningOnNote(noteId: string, pinned: boolean): Promise<Note> {
		const url = `/api/web/leads/notes/${noteId}/pinned`;
		return this.axiosInstance.put(url, {pinned}).then(resp => Note.fromJson(resp.data));
	}

	/**
	 * Add reminder to job note
	 * @param {string} jobId job identifier.
	 * @param {string} noteId note identifier.
	 * @param data
	 * @return {Promise<void>} promise containing notes for given lead.
	 */
	addJobReminder(jobId: string, noteId: string, data: SetReminderApiDto): Promise<void> {
		const url = `/api/web/jobs/${jobId}/notes/${noteId}/reminder`;
		return this.axiosInstance.put(url, data).then(() => {});
	}

	/**
	 * Fetch notes for given invoice.
	 * @return {Promise<AccountingNote[]>} promise containing notes for given invoice.
	 * @param invoiceId
	 */
	fetchInvoiceNotes(invoiceId: string): Promise<AccountingNote[]> {
		const url = `/api/web/accounting/invoices/${invoiceId}/notes`;
		return this.axiosInstance.get(url).then(resp =>
			resp.data.map((d: AccountingNoteApiDto) => AccountingNote.fromJson(d, InvoiceEventTypeEnum.Note)));
	}

	/**
	 * Create note for invoice.
	 * @param invoiceId invoice identifier
	 * @param noteText note content
	 * @return {Promise<AccountingNote>} promise containing newly added note.
	 */
	createInvoiceNote(invoiceId: string, noteText: string): Promise<AccountingNote> {
		const url = `/api/web/accounting/invoices/${invoiceId}/notes`;
		return this.axiosInstance.post(url, {noteText}).then(resp =>
			AccountingNote.fromJson(resp.data, InvoiceEventTypeEnum.Note));
	}

	/**
	 * Update invoice note.
	 * @param noteId note identifier.
	 * @param noteText note content
	 * @returns {Promise<AccountingNote>} Promise containing updated note.
	 */
	updateInvoiceNote(noteId: string, noteText: string): Promise<AccountingNote> {
		const url = `/api/web/accounting/invoices/notes/${noteId}`;
		return this.axiosInstance.put(url, {noteText}).then(resp =>
			AccountingNote.fromJson(resp.data, InvoiceEventTypeEnum.Note));
	}

	/**
	 * Delete invoice note.
	 * @param {string} noteId note identifier.
	 */
	deleteInvoiceNote(noteId: string): Promise<any> {
		const url = `/api/web/accounting/invoices/notes/${noteId}`;
		return this.axiosInstance.delete(url);
	}

	/**
	 * Fetch notes for given transaction.
	 * @return {Promise<AccountingNote[]>} promise containing notes for given transaction.
	 * @param transactionId
	 */
	fetchTransactionNotes(transactionId: string): Promise<AccountingNote[]> {
		const url = `/api/web/accounting/transactions/${transactionId}/notes`;
		return this.axiosInstance.get(url).then(resp =>
			resp.data.map((d: AccountingNoteApiDto) => AccountingNote.fromJson(d, TransactionEventTypeEnum.Note)));
	}

	/**
	 * Create note for transaction.
	 * @param transactionId transaction identifier
	 * @param noteText note content
	 * @return {Promise<AccountingNote>} promise containing newly added note.
	 */
	createTransactionNote(transactionId: string, noteText: string): Promise<AccountingNote> {
		const url = `/api/web/accounting/transactions/${transactionId}/notes`;
		return this.axiosInstance.post(url, {noteText}).then(resp =>
			AccountingNote.fromJson(resp.data, TransactionEventTypeEnum.Note));
	}

	/**
	 * Update transaction note.
	 * @param noteId note identifier.
	 * @param transactionId transaction identifier.
	 * @param noteText note content
	 * @returns {Promise<AccountingNote>} Promise containing updated note.
	 */
	updateTransactionNote(noteId: string, transactionId: string, noteText: string): Promise<AccountingNote> {
		const url = `/api/web/accounting/transactions/${transactionId}/notes/${noteId}`;
		return this.axiosInstance.put(url, {noteText}).then(resp =>
			AccountingNote.fromJson(resp.data, TransactionEventTypeEnum.Note));
	}

	/**
	 * Delete transaction note.
	 * @param {string} noteId note identifier.
	 * @param transactionId transaction identifier.
	 */
	deleteTransactionNote(noteId: string, transactionId: string,): Promise<any> {
		const url = `/api/web/accounting/transactions/${transactionId}/notes/${noteId}`;
		return this.axiosInstance.delete(url);
	}

	/**
	 * Fetch notes for given bill.
	 * @return {Promise<AccountingNote[]>} promise containing notes for given bill.
	 * @param billId
	 */
	fetchBillNotes(billId: string): Promise<AccountingNote[]> {
		const url = `/api/web/accounting/bills/${billId}/notes`;
		return this.axiosInstance.get(url).then(resp =>
			resp.data.map((d: AccountingNoteApiDto) => AccountingNote.fromJson(d, BillEventTypeEnum.Note)));
	}

	/**
	 * Create note for bill.
	 * @param billId bill identifier
	 * @param noteText note content
	 * @return {Promise<AccountingNote>} promise containing newly added note.
	 */
	createBillNote(billId: string, noteText: string): Promise<AccountingNote> {
		const url = `/api/web/accounting/bills/${billId}/notes`;
		return this.axiosInstance.post(url, {noteText}).then(resp =>
			AccountingNote.fromJson(resp.data, BillEventTypeEnum.Note));
	}

	/**
	 * Update bill note.
	 * @param noteId note identifier.
	 * @param noteText note content
	 * @returns {Promise<AccountingNote>} Promise containing updated note.
	 */
	updateBillNote(noteId: string, noteText: string): Promise<AccountingNote> {
		const url = `/api/web/accounting/bills/notes/${noteId}`;
		return this.axiosInstance.put(url, {noteText}).then(resp =>
			AccountingNote.fromJson(resp.data, BillEventTypeEnum.Note));
	}

	/**
	 * Delete bill note.
	 * @param {string} noteId note identifier.
	 */
	deleteBillNote(noteId: string): Promise<any> {
		const url = `/api/web/accounting/bills/notes/${noteId}`;
		return this.axiosInstance.delete(url);
	}
}
